import { useState } from 'react';
import { AppShell } from '@/components/AppShell';
import { Notice, Panel } from '@/components/Panel';
import { Moon, Sun } from 'lucide-react';
import { PanelGrid } from '@/components/charts';
import { SAMPLE_LIMIT, useLensData } from '@/data/source';
import { useSettings } from '@/data/settings';
import { SCHEMA_COMMON, THRESHOLD_GROUPS, type Threshold } from '@/app/settingsSchema';
import { lensFormatKrw, lensSampleCapNotice, lensSourceSummary } from '@/lens';

/**
 * 설정.
 *
 * ★★ **원본은 4,178줄이다** — FF 가 슬라이더 하나마다 위젯 트리를 복제했다.
 * 여기서는 `settingsSchema.ts` 의 **표 하나**를 `map` 한다.
 *
 * ⚠️ 기간 칩을 숨긴다 — 설정에는 기간 필터가 걸리지 않는다. 그리고 소스 요약은
 * **걸러진 수가 아니라 소스의 실제 건수**를 보여야 한다(`raw`).
 */
/**
 * 기준선 그룹의 축색 — ★ 4축 화면과 **같은 색**이다.
 *
 * ⚠️ 여기서 색을 새로 짓지 않는다. 다르면 설정에서 조정한 값이 어느 화면에
 * 걸리는지 눈으로 이을 수 없다.
 */
const GROUP_ACCENT: Record<string, string> = {
  완주율: 'var(--lens-axis-completion)',
  '진행 불가': 'var(--lens-axis-blocked)',
  'AI 오서술': 'var(--lens-axis-narration)',
  '회차당 원가': 'var(--lens-axis-cost)',
  '응답 시간': 'var(--lens-axis-cost)',
};

export function SettingsPage() {
  const { settings, set, resetThresholds, reload } = useSettings();
  // ⚠️ 설정에는 기간 필터가 없다 — 'all' 로 읽는다.
  const { raw, loading, error, mode, casualRuns } = useLensData('all');
  const [toast, setToast] = useState('');

  const say = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 4000);
  };

  return (
    <AppShell title="설정" subtitle="데이터 소스 · 기준선 임계값 · 이벤트 스키마 · 개인정보 정책.">
      {/* ⚠️ 읽기에 실패해도 설정 화면 자체는 떠야 한다 — 소스를 되돌릴 수단이
          **이 화면에** 있기 때문이다(Flutter 쪽 슬라이더 함정과 같은 교훈). */}
      {loading ? (
        <p className="text-body-md text-muted-foreground">불러오는 중…</p>
      ) : (
        <div className="space-y-xl">
          {toast !== '' ? <Notice text={toast} /> : null}

          {/* ⚠️ 표본 상한에 닿으면 지표를 그대로 믿으면 안 된다. 빈 문자열이면 숨는다. */}
          <Notice text={lensSampleCapNotice(raw, SAMPLE_LIMIT)} />

          <Panel
            title="데이터 소스"
            note="전환하면 이 화면은 바로 갱신되고, 다른 화면은 다음에 들어갈 때 반영됩니다."
          >
            <div className="flex flex-wrap items-center gap-sm">
              {(
                [
                  ['mock', '목업 데이터'],
                  ['firestore', 'Firestore'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => {
                    set('dataSourceMode', key);
                    reload();
                  }}
                  aria-pressed={settings.dataSourceMode === key}
                  className={
                    'rounded-pill border px-lg py-sm text-title-sm ' +
                    (settings.dataSourceMode === key
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-muted-foreground')
                  }
                >
                  {label}
                </button>
              ))}
              <button
                onClick={() => {
                  reload();
                  say('데이터를 다시 읽었습니다.');
                }}
                className="rounded-md border border-border bg-card px-lg py-sm text-title-sm"
              >
                다시 불러오기
              </button>
            </div>

            {/* ★ 걸러진 수가 아니라 **소스의 실제 건수**다. */}
            <p className="mt-lg text-body-md">{lensSourceSummary(raw, mode)}</p>

            {/* ★★ **버린 것을 밝힌다**(개발 회신 39 §4 · 사용자 확정 2026-10-04).
                ⚠️ 조용히 빠지면 「그런 회차가 없었다」와 구분이 안 된다 —
                   이 프로젝트에서 반복해 나온 실패 유형이다.
                ⚠️ 0 이면 줄을 그리지 않는다 — 「0회차 제외」는 알려 주는 것이
                   없고 자리만 차지한다. */}
            {casualRuns > 0 ? (
              <p className="mt-sm text-body-md text-muted-foreground">
                임의 플레이로 표시된 {casualRuns}회차는 지표에서 제외했습니다. 지인
                시연처럼 완주를 목표로 하지 않은 회차라, 세면 완주율과 진행 불가가
                실제보다 나빠 보입니다.
              </p>
            ) : null}

            {/* ⚠️ 읽기 실패를 **화면에 쓴다.** 복합 인덱스가 없으면 Firestore
                모드에서만 조회가 실패하고 목업은 멀쩡해서 코드 문제로 보인다. */}
            {error !== null ? (
              <p
                className="mt-md rounded-md px-lg py-md text-body-md"
                style={{ background: 'var(--lens-accent-error)', color: 'var(--lens-error)' }}
              >
                읽지 못했습니다 — {error}
              </p>
            ) : null}
          </Panel>

          <PanelGrid>
            <Panel
              title="화면 테마"
              icon={Sun}
              note="고른 값은 이 브라우저에 저장됩니다. 첫 페인트 전에 적용되므로 다음에 열 때 깜빡이지 않습니다."
            >
              <div className="flex flex-wrap items-center gap-sm">
                {(
                  [
                    ['dark', '어두운 계기판', Moon],
                    ['light', '밝은 판', Sun],
                  ] as const
                ).map(([key, label, Icon]) => (
                  <button
                    key={key}
                    onClick={() => {
                      set('theme', key);
                      say(key === 'light' ? '밝은 판으로 바꿨습니다.' : '어두운 계기판으로 바꿨습니다.');
                    }}
                    aria-pressed={settings.theme === key}
                    className={
                      'flex items-center gap-sm rounded-pill border px-lg py-sm text-title-sm ' +
                      (settings.theme === key
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-muted-foreground')
                    }
                  >
                    <Icon size={16} strokeWidth={1.9} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
              {/* ⚠️ 문구를 고쳤다 — 사이드바가 이제 테마를 따른다(2026-10-04).
                  ★ 집계를 바꾸면 그 화면의 설명도 같이 바꾼다는 규칙과 같은 자리다. */}
              <p className="mt-lg text-body-md text-muted-foreground">
                사이드바와 로고도 함께 바뀝니다. 축색은 어두운 판에 맞춰 밝혀 둔
                값이라 밝은 판에서는 조금 옅게 보입니다.
              </p>
            </Panel>
          </PanelGrid>

          {/* ★★ **축마다 카드 하나**(2026-10-03 개편). 전에는 열한 개 슬라이더가
              한 패널 안에서 800px 를 가로질렀다(사용자 지적 — 「가로로 넓어
              한눈에 안 들어온다」). ★ 축색이 붙어 **어느 화면의 기준선인지**도
              바로 읽힌다. */}
          <PanelGrid>
            {THRESHOLD_GROUPS.map((g) => (
              <Panel key={g.title} title={g.title} note={g.note} accent={GROUP_ACCENT[g.title]}>
                <div className="space-y-md">
                  {g.items.map((t) => (
                    <Slider
                      key={t.field}
                      t={t}
                      value={settings[t.field]}
                      onChange={(v) => set(t.field, v)}
                    />
                  ))}
                </div>
              </Panel>
            ))}
          </PanelGrid>

          <button
            onClick={() => {
              resetThresholds();
              say('기준선을 기본값으로 되돌렸습니다.');
            }}
            className="rounded-md border border-border bg-card px-lg py-sm text-title-sm"
          >
            기준선 기본값 복원
          </button>

          <PanelGrid>
            <TextSetting
            title="제외할 회차"
            note="시딩 표본이 첫 실데이터를 묻지 않게 합니다. Firestore 경로에서만 적용됩니다 — 목업 회차 이름이 r_* / dev_* 라 목업에 걸면 화면이 통째로 빕니다."
            label="접두 목록 (쉼표 구분)"
            value={settings.excludedRunPrefixes}
            onSave={(v) => {
              set('excludedRunPrefixes', v);
              say('제외할 회차 접두를 저장했습니다. 다시 불러오기를 누르세요.');
            }}
          />

          <TextSetting
            title="SORO 딥링크"
            note="장면 상세의 SORO 열기 버튼이 쓰는 기준 주소입니다. SORO 는 아직 개발되지 않아 링크만 만들어 두고 제어는 하지 않습니다."
            label="새 기준 주소"
            value={settings.soroBaseUrl}
            onSave={(v) => {
              set('soroBaseUrl', v);
              say('SORO 기준 주소를 저장했습니다.');
            }}
          />
          </PanelGrid>

          <Panel
            title="이벤트 스키마"
            note="LENS 가 읽는 events 컬렉션의 문서 규격입니다. 필드명이 실제와 다르면 매핑 함수 하나만 고치면 됩니다 — 화면과 집계는 손대지 않습니다."
          >
            <h3 className="mb-md text-title-md">공통 (모든 이벤트)</h3>
            {/* ★ 두 열로 접는다 — 한 열이면 오른쪽 절반이 통째로 빈다. */}
            <dl className="grid gap-x-xl gap-y-sm [grid-template-columns:auto_1fr] lg:[grid-template-columns:auto_1fr_auto_1fr]">
              {SCHEMA_COMMON.map(([field, desc]) => (
                <div key={field} className="contents">
                  <dt className="font-mono text-label-md text-primary">{field}</dt>
                  <dd className="text-body-sm text-muted-foreground">{desc}</dd>
                </div>
              ))}
            </dl>
          </Panel>

          <Panel title="개인정보 정책">
            <p className="text-body-md text-muted-foreground">
              LENS는 플레이어 입력 원문과 AI 서술 원문을 저장하거나 표시하지 않습니다. 이벤트
              스키마에 해당 필드가 존재하지 않습니다. 원문 조사가 필요하면 서버 측 별도 도구에서
              접근 권한을 분리해 처리합니다.
            </p>
          </Panel>
        </div>
      )}
    </AppShell>
  );
}

/**
 * 슬라이더 한 줄.
 *
 * ★ 오른쪽 숫자가 **실제 값**이다. ⚠️ Flutter 쪽은 손잡이가 화면 진입 시점
 * 값으로 굳어서 「복원 후 손잡이가 그대로면 화면을 다시 여세요」라는 안내가
 * 필요했다 — ★ React 에서는 상태가 곧 손잡이라 그 안내가 필요 없다.
 */
function Slider({
  t,
  value,
  onChange,
}: {
  t: Threshold;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-lg">
      <span className="w-32 shrink-0 text-title-sm text-muted-foreground">{t.label}</span>
      <input
        type="range"
        min={t.min}
        max={t.max}
        step={t.step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={t.label}
        className="min-w-0 flex-1 accent-[var(--lens-primary)]"
      />
      <span className="w-24 shrink-0 text-right text-title-sm tabular-nums">
        {t.krw ? lensFormatKrw(value) : value}
      </span>
    </div>
  );
}

/**
 * 문자열 설정 한 칸.
 *
 * ⚠️ 원본은 `TextField` 에 초기값을 바인딩할 수 없어 「현재 값」을 위에 따로
 * 보여주고 `onSubmitted` 로만 받았다. ★ React 에서는 제어 입력이라 그 우회가
 * 필요 없지만, **Enter 로 저장**하는 동작은 그대로 둔다(1판은 동작을 맞춘다).
 */
function TextSetting({
  title,
  note,
  label,
  value,
  onSave,
}: {
  title: string;
  note: string;
  label: string;
  value: string;
  onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <Panel title={title} note={note}>
      <p className="mb-md text-body-md">
        <span className="text-muted-foreground">현재 값 </span>
        <span className="font-mono">{value === '' ? '(비어 있음)' : value}</span>
      </p>
      <label className="block text-label-md text-muted-foreground">{label}</label>
      <div className="mt-xs flex gap-sm">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave(draft.trim());
          }}
          className="min-w-0 flex-1 rounded-md border border-border bg-[var(--lens-bg)] px-md py-sm text-body-md"
        />
        <button
          onClick={() => onSave(draft.trim())}
          className="shrink-0 rounded-md border border-border bg-card px-lg py-sm text-title-sm"
        >
          저장
        </button>
      </div>
      <p className="mt-xs text-body-sm text-muted-foreground">
        입력 후 Enter 를 누르거나 저장을 눌러야 반영됩니다.
      </p>
    </Panel>
  );
}
